import { OrniTransportError } from "./transport.ts";

export type SyncState = "current" | "pending" | "offline" | "error";

export interface SyncStreamStatus {
  stream: string;
  pendingCount: number;
  inFlightCount: number;
  failedCount: number;
  lastAckedSequence: number;
  latestSequence: number;
  oldestPendingSequence: number | null;
  lastError: string | null;
  nextRetryAt: string | null;
  updatedAt: string;
}

export interface SyncStatus {
  schemaVersion: number;
  transport: "local" | "cloud" | string;
  state: SyncState;
  generatedAt: string;
  streams: SyncStreamStatus[];
}

/** Decode the shared `/v1/sync/status` contract at a transport boundary. */
export function decodeSyncStatus(value: unknown): SyncStatus {
  const root = object(value);
  const state = text(root.state);
  if (!["current", "pending", "offline", "error"].includes(state)) {
    throw contractError("Sync status returned an unknown state.");
  }
  const streams = array(root.streams).map((item) => {
    const row = object(item);
    return {
      stream: text(row.stream),
      pendingCount: nonNegativeInteger(row.pendingCount),
      inFlightCount: nonNegativeInteger(row.inFlightCount),
      failedCount: nonNegativeInteger(row.failedCount),
      lastAckedSequence: nonNegativeInteger(row.lastAckedSequence),
      latestSequence: nonNegativeInteger(row.latestSequence),
      oldestPendingSequence: nullableNonNegativeInteger(row.oldestPendingSequence),
      lastError: nullableText(row.lastError),
      nextRetryAt: nullableText(row.nextRetryAt),
      updatedAt: text(row.updatedAt),
    };
  });
  return {
    schemaVersion: nonNegativeInteger(root.schemaVersion),
    transport: text(root.transport),
    state: state as SyncState,
    generatedAt: text(root.generatedAt),
    streams,
  };
}

function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw contractError("Expected an object.");
  return value as Record<string, unknown>;
}
function array(value: unknown): unknown[] {
  if (!Array.isArray(value)) throw contractError("Expected an array.");
  return value;
}
function text(value: unknown): string {
  if (typeof value !== "string") throw contractError("Expected text.");
  return value;
}
function nullableText(value: unknown): string | null {
  return value === null || value === undefined ? null : text(value);
}
function nonNegativeInteger(value: unknown): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0) throw contractError("Expected a non-negative integer.");
  return value as number;
}
function nullableNonNegativeInteger(value: unknown): number | null {
  return value === null || value === undefined ? null : nonNegativeInteger(value);
}
function contractError(message: string): OrniTransportError {
  return new OrniTransportError("CONTRACT_INVALID", message);
}
